import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import { Input, Label } from 'reactstrap';
import { Translate, I18n } from 'react-redux-i18n';
import Datetime from 'react-datetime';
import DropDownList from './DropDownList';
import GroupSelection from './GroupSelection';
import UploadFile from './UploadFile';
import SearchSelect from './SearchSelect';
import ViewModeControl from './ViewModeControl';
import {
    TYPE_TEXT,
    TYPE_EMAIL,
    TYPE_FILE,
    TYPE_HIDDEN,
    TYPE_NUMBER,
    TYPE_PASSWORD,
    TYPE_URL,
    TYPE_TEXTAREA,
    TYPE_RADIO,
    TYPE_CHECKBOX,
    TYPE_DATETIME,
    TYPE_SELECT,
    TYPE_SEARCH_DROPDOWNLIST,
} from './input-type';

export * from './input-type';

class CustomInput extends Component {
    static propTypes = {
        type: PropTypes.oneOf([
            TYPE_TEXT,
            TYPE_EMAIL,
            TYPE_FILE,
            TYPE_HIDDEN,
            TYPE_NUMBER,
            TYPE_PASSWORD,
            TYPE_URL,
            TYPE_TEXTAREA,
            TYPE_RADIO,
            TYPE_CHECKBOX,
            TYPE_DATETIME,
            TYPE_SELECT,
            TYPE_SEARCH_DROPDOWNLIST,
        ]),
        viewMode: PropTypes.bool,
        name: PropTypes.string,
        innerRef: PropTypes.shape(),
        // translation key
        caption: PropTypes.string,
        unitCaption: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.node,
        ]),
        required: PropTypes.bool,
        disabled: PropTypes.bool,
        value: PropTypes.oneOfType([
            PropTypes.node,
            PropTypes.arrayOf(PropTypes.shape({
                // translation key
                name: PropTypes.oneOfType([
                    PropTypes.string,
                    PropTypes.node
                ]),
                value: PropTypes.oneOfType([
                    PropTypes.node,
                    PropTypes.bool
                ]),
            })),
            PropTypes.instanceOf(moment)
        ]),
        selected: PropTypes.oneOfType([
            PropTypes.node,
            PropTypes.bool,
            PropTypes.arrayOf(PropTypes.node),
            PropTypes.arrayOf(PropTypes.bool)
        ]),
        onInput: PropTypes.func,
        withCaption: PropTypes.bool,
        isEnd: PropTypes.bool,
        assignedstyle: PropTypes.string,
    };

    static defaultProps = {
        type: TYPE_TEXT,
        viewMode: false,
        name: undefined,
        innerRef: undefined,
        caption: '',
        required: false,
        disabled: false,
        value: '',
        selected: undefined,
        onInput: () => {},
        withCaption: true,
        unitCaption: undefined,
        isEnd: false,
        assignedstyle: '',
    };

    constructor(props) {
        super(props);

        const { innerRef, value, selected, type, isEnd } = props;

        this.elInput = innerRef || React.createRef();
        this.simpleControlList = [
            TYPE_SELECT,
            TYPE_TEXT,
            TYPE_EMAIL,
            TYPE_HIDDEN,
            TYPE_NUMBER,
            TYPE_PASSWORD,
            TYPE_URL,
            TYPE_TEXTAREA,
        ];
        this.selectionControlList = [
            TYPE_RADIO,
            TYPE_CHECKBOX,
        ];

        this.customErrorMessage = '';
        this.hasValidated = false;

        this.state = {
            value,
            selected,
            invalidMessage: undefined,
        };

        this.elDatetime = (isEnd && type === TYPE_DATETIME) ? React.createRef() : undefined;
    }

    componentDidMount() {
        const input = this.elInput.current || {};
        input.setCustomErrorMessage = message => {
            input.setCustomValidity(message);
            this.customErrorMessage = message;
        };
    }

    handleInvalid = e => {
        e.preventDefault();

        this.hasValidated = true;

        this.checkValidity(e.currentTarget);
    }

    checkValidity = el => {
        const checkTypes = [
            'patternMismatch',
            'rangeOverflow',
            'rangeUnderflow',
            'tooLong',
            'tooShort',
            'typeMismatch',
            'valueMissing',
            'stepMismatch',
        ];
        let errorMessage = '';
        const { validity, title, step } = el;
        const { valid } = validity;

        if (!valid) {
            errorMessage = I18n.t('validation_error.generalInvalid');

            checkTypes.forEach(type => {
                if (validity[type]) {
                    errorMessage = I18n.t(`validation_error.${ type }`);

                    if (type === 'stepMismatch' && step < 1) {
                        const digits = (step.toString()).split('.')[1]?.length;
                        const ordinal = Number((digits.toString()).slice(-1));
                        errorMessage = I18n.t('validation_error.stepMismatchDecimal',
                            {
                                digits,
                                ordinal: (ordinal && ordinal <= 3) ?
                                    I18n.t(`validation_error.stepOrdinal.${ ordinal }`) :
                                    I18n.t('validation_error.stepOrdinal.th'),
                            });
                    }
                }
            });

            errorMessage = this.customErrorMessage || title || errorMessage;

            this.setState({
                invalidMessage: errorMessage,
            });
        }
        else {
            this.setState({
                invalidMessage: '',
            });
        }
    }

    handleInput = e => {
        const { onInput } = this.props;

        if (this.hasValidated) {
            this.checkValidity(e.currentTarget);
        }

        onInput && onInput(e);
    }
    handleDateTimeInputChange = e => {
        const { value } = e.target;
        const { type } = this.props;
        if (type === TYPE_DATETIME) {
            const newState = {
                value,
            };

            if (value instanceof moment) {
                newState.invalidMessage = '';
            }

            this.setState(newState);
        }
    };

    // NOTICE: this event is shared by native controllers and custom datetime
    // so `e` is may being the `event`, `key` or `moment`.
    handleChange = e => {
        const { onChange, type } = this.props;

        if (this.simpleControlList.indexOf(type) > -1) {
            this.setState({
                value: this.elInput.current.value,
            });
            this.handleInput(e);
        }
        else if (this.selectionControlList.indexOf(type) > -1) {
            return event => {
                const { selected = [] } = this.state;
                const realSelected = (type === TYPE_RADIO ? [] : [...selected]);
                const index = realSelected.indexOf(e);

                if (realSelected.indexOf(e) > -1) {
                    realSelected.splice(index, 1);
                }
                else {
                    realSelected.push(e);
                }

                this.setState({
                    selected: realSelected,
                });

                onChange && onChange(realSelected, event);
                this.handleInput(event);
            };
        }
        else if (type === TYPE_FILE && e.currentTarget.files.length > 0) {
            this.setState({
                value: e.currentTarget.files[0].name,
            });

            this.handleInput(e);
        }
        else if (type === TYPE_DATETIME) {
            this.handleEndDatetimeOnChange(e);
            const newState = {
                value: e,
            };
            if (e instanceof moment) {
                newState.invalidMessage = '';
            }
            this.setState(newState);
        }
        else if (type === TYPE_SEARCH_DROPDOWNLIST) {
            this.setState({
                invalidMessage: '',
            });
        }

        onChange && onChange(e);
    }

    getInputControlName = () => {
        const { type } = this.props;
        let el;

        switch (type) {
        case TYPE_RADIO:
        case TYPE_CHECKBOX:
            el = GroupSelection;
            break;
        case TYPE_DATETIME:
            el = Datetime;
            break;
        case TYPE_SELECT:
            el = DropDownList;
            break;
        case TYPE_FILE:
            el = UploadFile;
            break;
        case TYPE_SEARCH_DROPDOWNLIST:
            el = SearchSelect;
            break;
        case TYPE_TEXT:
        case TYPE_EMAIL:
        case TYPE_HIDDEN:
        case TYPE_NUMBER:
        case TYPE_PASSWORD:
        case TYPE_URL:
        default:
            el = Input;
        }

        return el;
    }

    handleEndDatetimeOnChange = datetime => {
        const { isEnd } = this.props;
        const { value } = this.state;
        if (!isEnd) return ;

        if (value !== 'Invalid date' && moment(datetime).isValid()) {
            this.elDatetime && this.elDatetime.current.setViewDate(moment(datetime));
        }
        else {
            if (datetime === '') {
                this.elDatetime && this.elDatetime.current.setViewDate(moment().endOf('day'));
            }
        }
    };

    getRealProps = () => {
        const {
            value,
            selected,
            open,
        } = this.state;
        const {
            type,
            name,
            required,
            disabled,
            readOnly,
        } = this.props;

        let { isEnd, ...inputProps } = this.props; // isEnd is only used by TYPE_DATETIME, need to eliminate
        let extentionProps = {};

        switch (type) {
        case TYPE_RADIO:
        case TYPE_CHECKBOX:
            inputProps = {
                ...inputProps,
                selected,
                onInvalid: this.handleInvalid,
            };
            break;
        case TYPE_DATETIME:
            const convertedValue = value === 'Invalid date' ? undefined : value;
            extentionProps = isEnd ? {
                ref: this.elDatetime,
                initialViewDate: value ? moment(value) : moment().endOf('day'),
            } : {};
            inputProps = {
                ...inputProps,
                value: convertedValue,
                open,
                closeOnSelect: true,
                initialValue: convertedValue,
                inputProps: {
                    name,
                    required,
                    disabled,
                    readOnly,
                    ref: this.elInput,
                    pattern: '[0-9]{4}-[0-9]{2}-[0-9]{2}[ ]?([0-9]{2}(:[0-9]{2}(:[0-9]{2})?)?)?$',
                    autoComplete: 'off',
                    onInvalid: this.handleInvalid,
                    onInput: this.handleInput,
                    onChange: this.handleDateTimeInputChange,
                },
            };

            break;
        case TYPE_SELECT:
            inputProps = {
                ...inputProps,
                onInvalid: this.handleInvalid,
            };
            break;
        default:
            inputProps = {
                ...inputProps,
                value: value === null ? undefined : value,
                selected,
                autoComplete: 'off',
                onInvalid: this.handleInvalid,
                onInput: this.handleInput,
            };
            break;
        }

        inputProps = {
            ...inputProps,
            ...extentionProps,
            innerRef: this.elInput,
            onChange: this.handleChange,
        };
        delete inputProps.viewMode;
        delete inputProps.withCaption;
        delete inputProps.unitCaption;

        return inputProps;
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        this.setState({
            value: nextProps.value,
            selected: nextProps.selected,
            invalidMessage: nextProps.invalidMessage,
        });
    }

    renderUnit() {
        const { unitCaption } = this.props;
        return (
            <>
                {
                    (typeof unitCaption === 'string') ? (
                        <Translate className="unitClass" tag="span" value={ unitCaption } />
                    ) : (
                        <>{ unitCaption }</>
                    )
                }
            </>
        );
    }

    renderErrorToolTip() {
        const { viewMode } = this.props;
        const { invalidMessage } = this.state;

        return viewMode ? null : <p className="error-message">{ invalidMessage }</p>;
    }

    renderControl() {
        const { viewMode, value, type, selected, format } = this.props;
        const inputProps = this.getRealProps();
        const Control = this.getInputControlName();
        return (
            viewMode ?
                (
                    <ViewModeControl
                        value={ value }
                        type={ type }
                        selected={ selected }
                        format={ format }
                    />
                ) :
                (
                    <Control { ...inputProps } />
                )
        );
    }

    render() {
        const { type, caption, required, viewMode, disabled, withCaption, assignedstyle } = this.props;
        const captionClass = classNames({
            caption: true,
            required: !viewMode && required,
        });
        const wrapperClass = classNames({
            [assignedstyle]: true,
            'control-group': true,
            'view-mode': viewMode,
            'disabled': disabled,
        });
        const controlClass = classNames({
            'control-wrapper': true,
            'full-width': !withCaption,
        });
        const Wrapper = (viewMode || [TYPE_RADIO, TYPE_CHECKBOX, TYPE_DATETIME].indexOf(type) > -1 ? 'div' : Label);
        return (
            type === TYPE_HIDDEN ?
                this.renderControl() :
                (
                    <Wrapper className={ wrapperClass }>
                        {
                            withCaption ?
                                (
                                    <Translate className={ captionClass } value={ caption } tag="h5" />
                                ) :
                                null
                        }
                        <div className={ controlClass }>
                            <span className="inline-wrapper">
                                { this.renderControl() }
                                { this.renderUnit() }
                            </span>
                            { this.renderErrorToolTip() }
                        </div>
                    </Wrapper>
                )
        );
    }
}

export default CustomInput;
